import IconButton from 'components/IconButton';
import Input from 'components/Inputs/Input';
import SimplePopup from 'components/SimplePopup';
import { useEffect, useState } from 'react';
import Loading from 'components/Loading';
import { observer } from 'mobx-react';
import { UserFireConfigModelType } from 'models/UserFireConfigModel';
import { UserFireConfigStoreType } from 'models/UserFireConfigStore';
import Select from 'components/Inputs/Select';

interface ISelectTopSettingsProps {
  setShowSettings: (value: boolean) => void;
  onSubmit?: () => void;
  onAbort?: () => void;
  organizationId: number;
  userFireConfig?: UserFireConfigModelType;
  userFireConfigStore: UserFireConfigStoreType;
}

function SelectTopSettingsPopup({
  setShowSettings,
  onAbort,
  onSubmit,
  organizationId,
  userFireConfig,
  userFireConfigStore
}: ISelectTopSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [benchmarkCount, setBenchmarkCount] = useState(5);
  const [selectedRegion, setSelectedRegion] = useState('Europa');
  const [selectedIndustry, setSelectedIndustry] = useState('Out of the Box');
  const [customRegion, setCustomRegion] = useState(false);
  const [customIndustry, setCustomIndustry] = useState(false);
  const [flavors, setFlavors] = useState<{ id: number; title: string }[]>([]);
  const [selectedFlavor, setSelectedFlavor] = useState<number | undefined>(
    undefined
  );
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const regionsResponse = await userFireConfigStore!.getRegions();
        const industriesResponse = await userFireConfigStore!.getIndustries();
        const flavorsResponse = await userFireConfigStore!.getFlavors();

        setRegions(regionsResponse);
        setIndustries(industriesResponse);
        setFlavors(flavorsResponse);

        if (userFireConfig) {
          setBenchmarkCount(
            userFireConfig.amount_of_benchmarks_per_painpoint || 2
          );
          setSelectedRegion(userFireConfig.selected_region || 'Europa');
          setSelectedIndustry(
            userFireConfig.selected_industry || 'Out of the Box'
          );
          const regionExists = regionsResponse.includes(
            userFireConfig.selected_region || 'Europa'
          );
          setCustomRegion(!regionExists);

          const industryExists = industriesResponse.includes(
            userFireConfig.selected_industry || 'Out of the Box'
          );
          setCustomIndustry(!industryExists);

          setSelectedFlavor(userFireConfig.flavor_id || undefined);
        }

        setLoading(false);
      } catch (error) {
        console.error('Failed to load settings:', error);
        setLoading(false);
      }
    };

    loadSettings();
  }, [userFireConfigStore, userFireConfig]);

  const handleSubmit = async () => {
    try {
      await userFireConfigStore!.updateUserFireConfig(organizationId, {
        amount_of_benchmarks_per_painpoint: benchmarkCount,
        selected_region: selectedRegion,
        selected_industry: selectedIndustry,
        flavor_id: selectedFlavor
      });

      if (!!onSubmit) {
        onSubmit();
      }
      setShowSettings(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };
  return (
    <SimplePopup
      onAbort={() => {
        if (!!onAbort) {
          onAbort();
        }
        setShowSettings(false);
      }}
      onSubmit={handleSubmit}
      headlineTextId="Settings(local)"
      submitTextId="Save"
      reduced={true}
      maxHeight={true}
      front={true}
    >
      {loading ? (
        <div style={{ padding: '2em 0' }}>
          <Loading inline={true} />
        </div>
      ) : (
        <div className="settings-container">
          <div className="settings-section">
            <h2 className="settings-title">Schwerpunkt</h2>
            <Select
              name="flavor"
              value={selectedFlavor}
              onChange={(e) => setSelectedFlavor(Number(e.target.value))}
            >
              <option value="" selected={selectedFlavor === undefined}>
                Schwerpunkt auswählen…
              </option>
              {flavors.map((flavor) => (
                <option
                  key={flavor.id}
                  value={flavor.id}
                  selected={selectedFlavor === flavor.id}
                >
                  {flavor.title}
                </option>
              ))}
            </Select>
          </div>
          <div className="settings-section">
            <h2 className="settings-title">Generierte Benchmarks</h2>
            <div className="settings-counter">
              <span className="settings-counter-label">Anzahl</span>
              <div className="settings-counter-controls">
                <IconButton
                  onClick={() =>
                    setBenchmarkCount((prev) => Math.max(1, prev - 1))
                  }
                  iconName="minus"
                  label="minus"
                />
                <input
                  className="settings-counter-input"
                  value={benchmarkCount}
                  readOnly
                />
                <IconButton
                  onClick={() =>
                    setBenchmarkCount((prev) => Math.min(10, prev + 1))
                  }
                  iconName="plus"
                  label="plus"
                />
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h2 className="settings-title">
              Länderregion für Benchmarks auswählen
            </h2>
            <div className="settings-selector">
              {regions.map((region) => (
                <button
                  key={region}
                  className={`settings-button ${
                    selectedRegion === region ? 'active' : ''
                  }`}
                  onClick={() => {
                    setSelectedRegion(region);
                    setCustomRegion(false);
                  }}
                >
                  {region}
                </button>
              ))}
            </div>
            <div className="settings-divider">oder</div>
            <Input
              normalFont={true}
              name="region"
              placeholder="Hier klicken um Sprachraum einzutippen"
              defaultValue={customRegion ? selectedRegion : ''}
              onChange={(e) => {
                setSelectedRegion(e.target.value);
                setCustomRegion(true);
              }}
            />
          </div>

          <div className="settings-section">
            <h2 className="settings-title">Branche für Benchmarks festlegen</h2>
            <div className="settings-selector">
              {industries.map((industry) => (
                <button
                  key={industry}
                  className={`settings-button ${
                    selectedIndustry === industry ? 'active' : ''
                  }`}
                  onClick={() => {
                    setSelectedIndustry(industry);
                    setCustomIndustry(false);
                  }}
                >
                  {industry}
                </button>
              ))}
            </div>
            <div className="settings-divider">oder</div>
            <Input
              normalFont={true}
              name="industry"
              placeholder="Hier klicken um Branche einzutippen"
              defaultValue={customIndustry ? selectedIndustry : ''}
              onChange={(e) => {
                setSelectedIndustry(e.target.value);
                setCustomIndustry(true);
              }}
            />
          </div>
        </div>
      )}
    </SimplePopup>
  );
}

export default observer(SelectTopSettingsPopup);
